import {
  CalendarDays,
  Inbox,
  ListTodo,
  MoreHorizontal,
  Sun,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { PRIMARY_VIEWS, VIEW_LABELS, type View } from '@/lib/views';

const TAB_ICONS = {
  daily: <Sun size={20} strokeWidth={1.5} aria-hidden />,
  plan: <CalendarDays size={20} strokeWidth={1.5} aria-hidden />,
  backlog: <ListTodo size={20} strokeWidth={1.5} aria-hidden />,
  inbox: <Inbox size={20} strokeWidth={1.5} aria-hidden />,
} as const;

interface Props {
  view: View;
  onChangeView: (view: View) => void;
  onOpenMore: () => void;
  inboxBadge?: boolean;
  inboxBadgeLabel?: string;
  moreActive?: boolean;
}

/**
 * Thumb-zone primary nav for phones. Four day-loop tabs plus More, never a
 * sideways-scrolling row. Hidden from `sm` up where the top bar owns nav.
 */
export function BottomTabBar({
  view,
  onChangeView,
  onOpenMore,
  inboxBadge,
  inboxBadgeLabel,
  moreActive,
}: Props) {
  return (
    <nav
      aria-label="Primary"
      className={cn(
        'material fixed inset-x-0 bottom-0 z-chrome border-t sm:hidden',
        'pb-[env(safe-area-inset-bottom,0px)]',
      )}
    >
      <div className="flex h-[3.25rem] items-stretch justify-around px-1">
        {PRIMARY_VIEWS.map((id) => {
          const active = view === id;
          const badge = id === 'inbox' && inboxBadge;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChangeView(id)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'pressable relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5',
                'text-label transition-colors duration-press',
                active ? 'font-medium text-accent' : 'text-text-secondary',
              )}
            >
              <span className="relative inline-flex">
                {TAB_ICONS[id as keyof typeof TAB_ICONS]}
                {badge && (
                  <span
                    aria-hidden
                    className="absolute -right-1 -top-0.5 size-dot rounded-full bg-accent"
                  />
                )}
              </span>
              <span className="truncate">{VIEW_LABELS[id]}</span>
              {badge && inboxBadgeLabel && (
                <span className="sr-only">, {inboxBadgeLabel}</span>
              )}
            </button>
          );
        })}

        <button
          type="button"
          onClick={onOpenMore}
          aria-label="More"
          aria-current={moreActive ? 'page' : undefined}
          className={cn(
            'pressable flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5',
            'text-label transition-colors duration-press',
            moreActive ? 'font-medium text-accent' : 'text-text-secondary',
          )}
        >
          <MoreHorizontal size={20} strokeWidth={1.5} aria-hidden />
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}
